import type { LatLngTuple, MissionRouteGuide, MissionRouteGuideStatus, RoutePathStop } from '@groupe-reca/contracts'

// Guide figé au démarrage par le serveur simulé (migration 20261118010000_route_paths_v1 : déclencheur
// `mission_route_guide_on_start_v1`). Géométrie FICTIVE de démonstration (coins à angle droit entre les entrées) :
// jamais un itinéraire calculé, jamais affichée hors du mode fixtures et du laboratoire.

const storageKey = 'reca-operator-v3-fixture-route-guide'

export interface GuideResidence {
  serviceLocationId: string
  order: number
  service: LatLngTuple | null
  flag: LatLngTuple | null
}

/** Statut forcé par `window.__recaFixtureServer.setRouteGuide` (essais de repli); `null` : comportement par défaut. */
export function forcedGuideStatus(): MissionRouteGuideStatus | null {
  try {
    const value = window.localStorage.getItem(storageKey)
    return value === 'READY' || value === 'NOT_CALCULATED' || value === 'STALE' || value === 'EXPIRED' ? value : null
  } catch {
    return null
  }
}

export function forceGuideStatus(status: MissionRouteGuideStatus | null): void {
  try {
    if (status) window.localStorage.setItem(storageKey, status)
    else window.localStorage.removeItem(storageKey)
  } catch {
    // Stockage indisponible : le comportement par défaut reste en vigueur.
  }
}

function meters(a: LatLngTuple, b: LatLngTuple): number {
  const dLat = (b[0] - a[0]) * 111_320
  const dLng = (b[1] - a[1]) * 111_320 * Math.cos((a[0] * Math.PI) / 180)
  return Math.hypot(dLat, dLng)
}

export const fixtureDepot = { label: 'Groupe RECA — 962 Rue Labelle, Saint-Jérôme (démo)', point: [45.790931, -74.007897] as LatLngTuple }

export function fixtureRouteGuide(
  residences: readonly GuideResidence[],
  options: { withPath: boolean; nextRoute: MissionRouteGuide['nextRoute']; now: Date },
): MissionRouteGuide {
  const stops: RoutePathStop[] = residences.flatMap((residence) => {
    const point = residence.flag ?? residence.service
    if (!point || !residence.service) return []
    return [
      {
        serviceLocationId: residence.serviceLocationId,
        order: residence.order,
        point,
        pointSource: residence.flag ? ('FLAG' as const) : ('SERVICE' as const),
        service: residence.service,
      },
    ]
  })
  const status: MissionRouteGuideStatus = forcedGuideStatus() ?? (options.withPath && stops.length >= 2 ? 'READY' : 'NOT_CALCULATED')
  const legs = stops.slice(1).map((to, index) => {
    const from = stops[index] ?? to
    // Dernière portion le long de la « rue » de la résidence (latitude de son entrée) : le côté de rue se démontre.
    const corner: LatLngTuple = [to.point[0], from.point[1]]
    const distance = meters(from.point, corner) + meters(corner, to.point)
    return {
      fromServiceLocationId: from.serviceLocationId,
      toServiceLocationId: to.serviceLocationId,
      distanceMeters: Math.round(distance),
      durationSeconds: Math.round(distance / 8),
      geometry: [from.point, corner, to.point],
    }
  })
  const validatedAt = new Date(options.now.getTime() - 86_400_000).toISOString()
  return {
    status,
    path:
      status === 'READY'
        ? {
            pathId: '90000000-0000-4000-a000-000000000001',
            version: 2,
            provider: 'MAPBOX_DIRECTIONS',
            profile: 'driving',
            validatedAt,
            expiresAt: new Date(options.now.getTime() + 29 * 86_400_000).toISOString(),
            stops,
            legs,
            distanceMeters: legs.reduce((sum, leg) => sum + leg.distanceMeters, 0),
            durationSeconds: legs.reduce((sum, leg) => sum + leg.durationSeconds, 0),
          }
        : null,
    depot: fixtureDepot,
    nextRoute: options.nextRoute,
  }
}
